import { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import type { EstadoCertificadosApi } from '@/utils/avisoCertificado';

const EVENTO_CAMBIO = 'certificados:cambio';

/**
 * Avisa a quien muestre el estado de los certificados (el aviso global) de que
 * algo cambió: subida, activación, borrado, migración o tareas.
 */
export function notificarCambioCertificados(): void {
  window.dispatchEvent(new Event(EVENTO_CAMBIO));
}

/**
 * Estado resumido de los certificados (GET /api/certificados/estado). Se
 * carga al montar y cada vez que alguien llama a `notificarCambioCertificados`.
 * Hasta la primera carga `estado` es `null` (no se avisa de nada). Si una recarga
 * falla se conserva el estado anterior: un fallo transitorio no borra un aviso.
 * Solo se aplica la respuesta de la última petición.
 */
export function useEstadoCertificados() {
  const { fetchWithAuth } = useUser();
  const [estado, setEstado] = useState<EstadoCertificadosApi | null>(null);

  const ultimaPeticion = useRef(0);

  const recargar = useCallback(async () => {
    const numero = ++ultimaPeticion.current;
    try {
      const respuesta = await fetchWithAuth('/certificados/estado');
      if (!respuesta.ok) return;
      const datos = await respuesta.json();
      // Una petición posterior (o el desmontaje) invalida esta respuesta.
      if (numero === ultimaPeticion.current) setEstado(datos);
    } catch (error) {
      console.error('Error al cargar el estado de los certificados:', error);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    recargar();
    window.addEventListener(EVENTO_CAMBIO, recargar);
    return () => {
      window.removeEventListener(EVENTO_CAMBIO, recargar);
      // Guarda defensiva sin test propio: en React 18 un `setState` tras desmontar no hace nada
      // ni avisa, así que no hay efecto observable que distinga tenerla de quitarla.
      ultimaPeticion.current++;
    };
  }, [recargar]);

  return { estado, recargar };
}
